import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { BRAND } from "@/data";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", intent: "both", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to be contacted to continue.");
      return;
    }
    setLoading(true);
    try {
      await submitContact({ ...form, consent });
      setDone(true);
      toast.success("Message sent! Brett will be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please call 720-776-2502.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <Reveal>
          <p className="overline text-kw-red mb-4">Start Your Plan</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-kw-ink leading-[1.02]">
            Let's replace uncertainty with a proven plan.
          </h2>
          <p className="mt-6 text-neutral-600 leading-relaxed max-w-md">
            Tell me a bit about your goals. Whether you're buying, selling, or both, I'll map out the
            exact next steps — no pressure, no obligation.
          </p>
          <div className="mt-8 space-y-1">
            <p className="text-sm text-neutral-500">Call or text directly</p>
            <a href={BRAND.phoneHref} className="font-serif text-3xl text-kw-red hover:text-kw-redhover transition-colors">{BRAND.phone}</a>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          {done ? (
            <div data-testid="contact-success" className="bg-kw-ink text-white p-10">
              <h3 className="font-serif text-3xl mb-2">Thank you, {form.name.split(" ")[0]}.</h3>
              <p className="text-neutral-300 text-sm leading-relaxed">
                Your message is on its way to Brett. Expect a reply at {form.email} shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="border border-neutral-200 p-7 sm:p-9">
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Full name"
                  className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors" />
                <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone"
                  className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors" />
              </div>
              <input data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder="Email address"
                className="mt-3 w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors" />
              <select data-testid="contact-intent-select" value={form.intent} onChange={set("intent")}
                className="mt-3 w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors bg-white text-neutral-700">
                <option value="seller">I want to sell</option>
                <option value="buyer">I want to buy</option>
                <option value="both">Buying and selling</option>
              </select>
              <textarea data-testid="contact-message-input" value={form.message} onChange={set("message")} rows={4} placeholder="Tell me about your goals (optional)"
                className="mt-3 w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors resize-none" />
              <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="contact-consent-label">
                <input
                  type="checkbox"
                  data-testid="contact-consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-kw-red cursor-pointer"
                />
                <span className="text-xs leading-relaxed text-neutral-500">
                  I agree to be contacted by Brett Weller - Keller Williams DTC via call, email,
                  and text for real estate services. To opt out, you can reply 'stop' at any time or
                  reply 'help' for assistance. You can also click the unsubscribe link in the emails.
                  Message and data rates may apply. Message frequency may vary.{" "}
                  <Link to="/privacy" className="text-kw-red hover:text-kw-redhover underline underline-offset-2">
                    Privacy Policy
                  </Link>.
                </span>
              </label>
              <button type="submit" disabled={loading} data-testid="contact-submit-button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-kw-red hover:bg-kw-redhover disabled:opacity-70 text-white py-3.5 font-semibold tracking-wide transition-colors duration-300">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <>Send My Message <ArrowRight size={17} /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
